import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';

export type CalendarScope = 'me' | 'team' | 'tenant';

export type CalendarSource =
  | 'essay'
  | 'quarterly-report'
  | 'quarterly-scoring'
  | 'perf-review'
  | 'review-360'
  | 'goal';

export interface CalendarItem {
  id: string;
  type: 'deadline' | 'period';
  source: CalendarSource;
  /** Заголовок собирает фронт: сервер шлёт ключ i18n и подстановки. */
  titleKey: string;
  titleParams: Record<string, string | number>;
  start: string;
  /** Исключающая граница периода. */
  end?: string;
  isAllDay: boolean;
  person?: { id: string; name: string };
  isDone: boolean;
  isOverdue: boolean;
  url: string;
}

export const useCalendar = (from: string, to: string, scope: CalendarScope) =>
  useQuery({
    queryKey: ['calendar', from, to, scope],
    queryFn: async (): Promise<CalendarItem[]> =>
      api(`/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&scope=${scope}`),
  });

/** Пояс организации и права на виды «Команда» и «Организация» — меняются редко, держим дольше обычного. */
export const useCalendarContext = () =>
  useQuery({
    queryKey: ['calendar', 'context'],
    queryFn: async (): Promise<{
      timeZone: string;
      hasTeamScope: boolean;
      hasCompanyScope: boolean;
    }> => api('/calendar/context'),
    staleTime: 5 * 60 * 1000,
  });

export const useFeedToken = () =>
  useQuery({
    queryKey: ['calendar', 'feed-token'],
    queryFn: async (): Promise<{ hasToken: boolean; createdAt?: string; lastUsedAt?: string }> =>
      api('/calendar/feed-token'),
  });

export const useRotateFeedToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ url: string }> =>
      api('/calendar/feed-token/rotate', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar', 'feed-token'] });
    },
  });
};
